/**
 * Voice Mode API slice (Realtime sessions, heartbeat, vision).
 * Leaf module (Perf 3.1.b/e Step 6). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { Platform } from 'react-native';
import { API_HOST } from '@/constants/config';
import { devLog } from '@/lib/devLog';
import { fetchWithAuth, fetchWithTimeout, ApiError, getAuthStore } from './client';

export const voiceApi = {
  // Check if voice mode is available for current user
  getStatus: async () => {
    const data = await fetchWithAuth('/api/voice/status');
    if (data.error) {
      throw new ApiError(data.message || 'Failed to check voice status', data.status || 400, data);
    }
    return data;
  },

  // Get available voice-mode voices. Web/iOS/Android receive OpenAI Realtime
  // voices; legacy socket fallback paths may still receive OpenAI TTS voices.
  getVoices: async () => {
    const data = await fetchWithAuth('/api/voice/voices');

    if (data.error) {
      throw new ApiError(data.message || 'Failed to fetch voices', 400, data);
    }

    const defaultVoice = data.defaultVoice || 'marin';
    return {
      voices: data.voices || [],
      defaultVoice,
      defaultVoiceId: defaultVoice, // Backwards compatibility for store selector
      provider: data.provider || 'openai'
    };
  },

  // Start a new voice session. The backend creates the VoiceSession record;
  // web/iOS/Android then use it for OpenAI Realtime WebRTC SDP exchange.
  startSession: async (voiceId = null, conversationId = null) => {
    const body = {};
    if (voiceId) body.voiceId = voiceId;
    if (conversationId) body.conversationId = conversationId;
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timezone) body.timezone = timezone;
    } catch (_) { /* optional */ }

    devLog('[VoiceApi] POST /api/voice/session', {
      voiceId: body.voiceId || '(default)',
      conversationId: body.conversationId || '(new)',
      timezone: body.timezone || '(unknown)',
      apiHost: API_HOST
    });

    const data = await fetchWithAuth('/api/voice/session', {
      method: 'POST',
      body
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to start voice session', data.status || 400, data);
    }

    // Voice session response. No longer includes signedUrl/agentId.
    return {
      success: data.success,
      session: data.session,
      voiceSessionToken: data.voiceSessionToken,
      voiceId: data.voiceId || 'marin',
      provider: data.provider || 'openai-realtime',
      config: data.config, // Contains pricing, audio config, and VAD config
      credits: data.credits,
      conversationId: data.conversationId,
    };
  },

  // Exchange a WebRTC offer SDP for an OpenAI Realtime answer SDP.
  createRealtimeAnswer: async (sessionId, offerSdp) => {
    const authStore = getAuthStore().getState();
    let token = authStore.token;

    if (token && authStore.isTokenExpired()) {
      token = await authStore.getValidToken();
    }

    const response = await fetchWithTimeout(`${API_HOST}/api/voice/session/${sessionId}/realtime/sdp`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/sdp',
        'X-Platform': Platform.OS,
      },
      body: offerSdp,
    }, 30000);

    const text = await response.text();

    if (!response.ok) {
      let parsed = null;
      try { parsed = JSON.parse(text); } catch (_) {}
      throw new ApiError(
        parsed?.message || 'Failed to connect to realtime voice model',
        response.status,
        parsed,
      );
    }

    return text;
  },

  // Send heartbeat for credit tracking (call every 30 seconds)
  // Supports activity-based tracking with additional options
  sendHeartbeat: async (sessionId, durationSeconds, options = {}) => {
    const body = {
      durationSeconds,
      // Activity-based tracking fields
      totalSessionSeconds: options.totalSessionSeconds,
      isActive: options.isActive,
      isDisconnected: options.isDisconnected,
    };

    const data = await fetchWithAuth(`/api/voice/session/${sessionId}/heartbeat`, {
      method: 'POST',
      body,
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to send heartbeat', 400, data);
    }

    return {
      credits: data.credits,
      warnings: data.warnings
    };
  },

  // End voice session
  // Supports activity-based tracking with additional options
  endSession: async (sessionId, durationSeconds, options = {}) => {
    const body = {
      durationSeconds,
      // Activity-based tracking fields
      totalSessionSeconds: options.totalSessionSeconds,
      isActive: options.isActive,
      isDisconnected: options.isDisconnected,
    };

    const data = await fetchWithAuth(`/api/voice/session/${sessionId}/end`, {
      method: 'POST',
      body,
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to end voice session', 400, data);
    }

    return {
      session: data.session,
      credits: data.credits
    };
  },

  // Change voice during an active session
  changeVoice: async (sessionId, voiceId) => {
    const data = await fetchWithAuth(`/api/voice/session/${sessionId}/voice`, {
      method: 'POST',
      body: { voiceId }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to change voice', data.status || 400, data);
    }

    return {
      success: data.success,
      voiceId: data.voiceId,
    };
  },

  // Save a transcript message to the conversation
  saveTranscript: async (sessionId, conversationId, role, content, citations = null) => {
    const body = { conversationId, role, content };
    if (Array.isArray(citations) && citations.length > 0) {
      body.citations = citations;
    }
    const data = await fetchWithAuth(`/api/voice/session/${sessionId}/transcript`, {
      method: 'POST',
      body,
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to save transcript', data.status || 400, data);
    }

    return data;
  },

  // Live web search for voice mode (Realtime web_search tool)
  search: async (sessionId, query) => {
    const data = await fetchWithAuth(`/api/voice/session/${sessionId}/search`, {
      method: 'POST',
      body: { query },
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to run web search', data.status || 400, data);
    }

    return {
      success: !!data.success,
      query: data.query || query,
      results: Array.isArray(data.results) ? data.results : [],
      citations: Array.isArray(data.citations) ? data.citations : [],
      context: data.context || null,
      empty: !!data.empty,
    };
  },

  // ==================== Vision API ====================

  // Start a vision session for the current voice session
  startVision: async (sessionId, source = 'camera') => {
    const data = await fetchWithAuth('/api/voice/vision/start', {
      method: 'POST',
      body: { sessionId, source }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to start vision session', data.status || 400, data);
    }

    return {
      success: data.success,
      visionActive: data.visionActive,
      config: data.config || null, // { maxFrameRate, maxFrameSizeKB, creditsPerFrame }
    };
  },

  // Send a video frame for analysis
  sendVisionFrame: async (sessionId, frameData) => {
    const data = await fetchWithAuth('/api/voice/vision/frame', {
      method: 'POST',
      body: { sessionId, frame: frameData }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to send vision frame', data.status || 400, data);
    }

    return {
      success: data.success,
      visualContext: data.visualContext || null,
      cached: data.cached || false,
      skipped: data.skipped || false,
      frameNumber: data.frameNumber,
      analysisError: data.analysisError || null,
    };
  },

  // Stop the vision session
  stopVision: async (sessionId) => {
    const data = await fetchWithAuth('/api/voice/vision/stop', {
      method: 'POST',
      body: { sessionId }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to stop vision session', data.status || 400, data);
    }

    return {
      success: data.success,
      framesAnalyzed: data.framesAnalyzed || 0,
      duration: data.duration || 0,
    };
  },

  // Get vision session status
  getVisionStatus: async (sessionId) => {
    const data = await fetchWithAuth(`/api/voice/vision/status/${sessionId}`, {
      method: 'GET'
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to get vision status', data.status || 400, data);
    }

    return {
      active: data.visionActive,
      source: data.source,
      frameCount: data.frameCount || 0,
      lastContext: data.lastContext || null,
      startedAt: data.startedAt || null,
    };
  },
};
